import React, { useState, useEffect } from 'react';
import { cn } from '../../../lib/utils';
import { Edit2, RotateCcw, Check, X, MapPin, Clock } from 'lucide-react';

const SchemaTab = ({ schema, summary, isFullScreen = false, onUpdateMetadata, onRebuildMetadata, spatialColumns, temporalColumns }) => {
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditingSummary] = useState(summary || '');
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [isConfirmingRebuild, setIsConfirmingRebuild] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');

  useEffect(() => {
    setEditingSummary(summary || '');
  }, [summary]);

  if (!schema) return null;

  // Helper function to get column metadata information
  const getColumnMetadata = (columnName) => {
    const metadata = {
      spatial: null,
      temporal: null
    };

    // Check spatial columns (handle both old format - array of strings - and new format - array of objects)
    const spatialCols = spatialColumns || [];
    const spatialEntry = spatialCols.find(col => {
      if (typeof col === 'string') {
        return col === columnName;
      } else if (col && typeof col === 'object') {
        return col.name === columnName || col.lat_column === columnName || col.lon_column === columnName;
      }
      return false;
    });

    if (spatialEntry) {
      if (typeof spatialEntry === 'string') {
        metadata.spatial = { type: 'spatial', display: 'Spatial Column' };
      } else if (spatialEntry && typeof spatialEntry === 'object') {
        if (spatialEntry.type === 'h3') {
          metadata.spatial = {
            type: 'h3',
            resolution: spatialEntry.resolution,
            display: `H3 Hex (Resolution ${spatialEntry.resolution})`
          };
        } else if (spatialEntry.type === 'lat_lon') {
          if (spatialEntry.lat_column === columnName) {
            metadata.spatial = {
              type: 'lat_lon',
              role: 'lat',
              pairColumn: spatialEntry.lon_column,
              display: `Latitude (paired with ${spatialEntry.lon_column})`
            };
          } else if (spatialEntry.lon_column === columnName) {
            metadata.spatial = {
              type: 'lat_lon',
              role: 'lon',
              pairColumn: spatialEntry.lat_column,
              display: `Longitude (paired with ${spatialEntry.lat_column})`
            };
          } else {
            metadata.spatial = {
              type: 'lat_lon',
              display: `Lat/Lon Pair (${spatialEntry.lat_column}, ${spatialEntry.lon_column})`
            };
          }
        } else if (spatialEntry.type === 'x_y') {
          if (spatialEntry.lat_column === columnName) {
            metadata.spatial = {
              type: 'x_y',
              role: 'x',
              pairColumn: spatialEntry.lon_column,
              display: `X Coordinate (paired with ${spatialEntry.lon_column})`
            };
          } else if (spatialEntry.lon_column === columnName) {
            metadata.spatial = {
              type: 'x_y',
              role: 'y',
              pairColumn: spatialEntry.lat_column,
              display: `Y Coordinate (paired with ${spatialEntry.lat_column})`
            };
          } else {
            metadata.spatial = {
              type: 'x_y',
              display: `X/Y Pair (${spatialEntry.lat_column}, ${spatialEntry.lon_column})`
            };
          }
        } else if (spatialEntry.type === 'wkt') {
          metadata.spatial = {
            type: 'wkt',
            display: 'WKT Geometry'
          };
        } else if (spatialEntry.type === 'geometry') {
          metadata.spatial = {
            type: 'geometry',
            display: 'Geometry'
          };
        } else {
          metadata.spatial = {
            type: 'spatial',
            display: 'Spatial Column'
          };
        }
      }
    }

    // Check temporal columns (handle both old format - array of strings - and new format - array of objects)
    const temporalCols = temporalColumns || [];
    const temporalEntry = temporalCols.find(col => {
      if (typeof col === 'string') {
        return col === columnName;
      } else if (col && typeof col === 'object') {
        return col.name === columnName;
      }
      return false;
    });

    if (temporalEntry) {
      if (typeof temporalEntry === 'string') {
        metadata.temporal = { type: 'temporal', display: 'Temporal Column' };
      } else if (temporalEntry && typeof temporalEntry === 'object' && temporalEntry.type) {
        const typeLabels = {
          'date': 'Date',
          'timestamp': 'Timestamp',
          'time': 'Time',
          'datetime': 'DateTime'
        };
        metadata.temporal = {
          type: temporalEntry.type,
          display: typeLabels[temporalEntry.type] || temporalEntry.type.charAt(0).toUpperCase() + temporalEntry.type.slice(1)
        };
      } else {
        metadata.temporal = { type: 'temporal', display: 'Temporal Column' };
      }
    }

    return metadata;
  };

  const handleSaveSummary = () => {
    onUpdateMetadata({ summary: editedSummary });
    setIsEditingSummary(false);
  };

  const handleSaveColumnDescription = (columnName) => {
    const newSchema = schema.map(col =>
      col.name === columnName ? { ...col, description: editedDescription } : col
    );
    onUpdateMetadata({ schema: newSchema });
    setEditingColumn(null);
  };

  const handleRebuild = async () => {
    setIsConfirmingRebuild(false);
    setIsRebuilding(true);
    try {
      await onRebuildMetadata();
    } finally {
      setIsRebuilding(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {(summary || isFullScreen) && (
        <div className={cn(
          "border-b border-gray-100 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-900/10",
          isFullScreen ? "p-8" : "p-4"
        )}>
          <div className="flex items-center justify-between mb-3">
            {isFullScreen && (
              <h4 className="text-[11px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-[0.3em]">Table Description</h4>
            )}
            {isFullScreen && (
              <div className="flex items-center gap-2">
                {isConfirmingRebuild ? (
                  <div className="flex items-center gap-3 px-4 py-1.5 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-xl animate-in fade-in zoom-in-95 duration-200">
                    <span className="text-[9px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest">Regenerate everything?</span>
                    <div className="flex items-center gap-1.5 border-l border-red-100 dark:border-red-900/30 pl-3 ml-1">
                      <button
                        onClick={() => setIsConfirmingRebuild(false)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                      >
                        <X size={14} />
                      </button>
                      <button
                        onClick={handleRebuild}
                        className="p-1 text-red-600 hover:text-red-700 transition-colors"
                      >
                        <Check size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsConfirmingRebuild(true)}
                    disabled={isRebuilding}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-[9px] font-black text-gray-500 hover:text-emerald-600 hover:border-emerald-500 transition-all disabled:opacity-50"
                  >
                    <RotateCcw size={12} className={cn(isRebuilding && "animate-spin")} />
                    REGENERATE
                  </button>
                )}
              </div>
            )}
          </div>
          {isEditingSummary ? (
            <div className="flex items-start gap-3 animate-in fade-in slide-in-from-top-1 duration-200">
              <textarea
                autoFocus
                value={editedSummary}
                onChange={(e) => setEditingSummary(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveSummary();
                  }
                  if (e.key === 'Escape') {
                    setIsEditingSummary(false);
                    setEditingSummary(summary || '');
                  }
                }}
                className="flex-1 bg-white dark:bg-gray-900 border border-emerald-500 rounded-xl p-4 text-sm font-medium text-gray-700 dark:text-gray-200 focus:ring-4 focus:ring-emerald-500/10 outline-none min-h-[100px] resize-none"
                placeholder="Describe what this dataset is for..."
              />
              <div className="flex flex-col gap-2 mt-1">
                <button
                  onClick={handleSaveSummary}
                  className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all shadow-sm"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={() => {
                    setIsEditingSummary(false);
                    setEditingSummary(summary || '');
                  }}
                  className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 rounded-lg hover:text-gray-600 transition-all shadow-sm"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ) : (
            <div className="group relative">
              <p className={cn(
                "text-gray-600 dark:text-gray-400 font-medium leading-relaxed italic",
                isFullScreen ? "text-sm ml-1" : "text-xs"
              )}>
                "{summary || 'No description provided for this table yet.'}"
              </p>
              {isFullScreen && (
                <button
                  onClick={() => setIsEditingSummary(true)}
                  className="absolute -right-2 -top-2 p-2 bg-white dark:bg-gray-800 border border-emerald-100 dark:border-emerald-900/30 rounded-lg text-emerald-600 opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                >
                  <Edit2 size={12} />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-auto custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[600px]">
            <thead className="sticky top-0 bg-white dark:bg-gray-950 z-10">
              <tr className="border-b border-gray-100 dark:border-gray-800 shadow-sm">
                <th className="px-4 py-3 text-left">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Column</span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Type</span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Metadata</span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Description</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50/50 dark:divide-gray-900/20">
              {schema.map((column) => {
                const metadata = getColumnMetadata(column.name);
                return (
                <tr key={column.name} className="group hover:bg-gray-50/50 dark:hover:bg-emerald-900/5 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-xs font-bold text-gray-900 dark:text-white font-mono tracking-tight">{column.name}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-lg uppercase tracking-tight">
                      {column.type}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {(metadata.spatial || metadata.temporal) ? (
                      <div className="flex flex-wrap items-center gap-2">
                        {metadata.spatial && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            <MapPin size={12} className="flex-shrink-0" />
                            <span className="text-[10px] font-semibold">
                              {metadata.spatial.display}
                            </span>
                          </div>
                        )}
                        {metadata.temporal && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                            <Clock size={12} className="flex-shrink-0" />
                            <span className="text-[10px] font-semibold">
                              {metadata.temporal.display}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">—</span>
                    )}
                  </td>
                  <td className={cn("text-gray-500 dark:text-gray-400 font-medium", isFullScreen ? "py-5 px-8 text-sm" : "py-2 px-4 text-xs")}>
                    {isFullScreen ? (
                      editingColumn === column.name ? (
                        <div className="flex items-center gap-2">
                          <input
                            autoFocus
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveColumnDescription(column.name);
                              if (e.key === 'Escape') setEditingColumn(null);
                            }}
                            className="flex-1 bg-white dark:bg-gray-900 border border-emerald-500 rounded-lg px-3 py-1.5 text-sm focus:ring-4 focus:ring-emerald-500/10 outline-none"
                          />
                          <button onClick={() => handleSaveColumnDescription(column.name)} className="p-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all">
                            <Check size={12} />
                          </button>
                          <button onClick={() => setEditingColumn(null)} className="p-1.5 bg-gray-100 dark:bg-gray-800 text-gray-400 rounded-lg hover:text-gray-600 transition-all">
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => {
                            setEditingColumn(column.name);
                            setEditedDescription(column.description || '');
                            setIsEditingSummary(false);
                          }}
                          className="cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center justify-between group/desc"
                        >
                          <span>{column.description || 'No description available'}</span>
                          <Edit2 size={12} className="opacity-0 group-hover/desc:opacity-100 transition-opacity ml-4" />
                        </div>
                      )
                    ) : (
                      <span>{column.description || 'No description available'}</span>
                    )}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SchemaTab;
